import { redactLinkCredentials, redactLinkCredentialsDeep } from './redact-link-credentials';

/**
 * #665: o OpenReplay grava a URL de toda requisição e a URL da página. No
 * checkout isso inclui o polling de `/payments/status/<pedido>` (o nº do pedido
 * abre `/pix/<pedido>`) e, se a sessão passar por elas, as páginas de link.
 * Mesma regra que o #626/#627 aplicou no rastreador de erros
 * (`redactLinkCredentials`): só o segmento-credencial vira `[REDACTED]`;
 * método, status, tempo e o resto da URL seguem para depurar sessão.
 */

// Cabeçalho que é credencial por si: some inteiro (o padrão do tracker já
// ignora cookie/set-cookie/authorization; aqui vale mesmo se a lista mudar).
const CABECALHOS_FORA = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
]);

export interface RequisicaoGravada {
  readonly status: number;
  readonly method: string;
  url: string;
  request: { body: string | null; headers: Record<string, string> };
  response: { body: unknown; headers: Record<string, string> };
}

// Revisão do #665: o Purchase do Pixel (`metaPurchase.ts`) leva o nº do pedido
// na query de `facebook.com/tr` (`eid=purchase_<pedido>` e
// `cd[content_ids]=["<pedido>"]`), e o OpenReplay grava essa URL no log de
// rede e no Timing. A regra de link só olha o caminho.
const PARAMS_COM_PEDIDO = new Set(['eid', 'cd[content_ids]']);

function decodificar(texto: string): string {
  try {
    return decodeURIComponent(texto.replace(/\+/g, ' '));
  } catch {
    return texto;
  }
}

/**
 * Query da URL: valor de `PARAMS_COM_PEDIDO` vira `[REDACTED]`, e valor que é
 * URL codificada (`dl=https%3A%2F%2F…%2Fpix%2F<pedido>`) passa pela regra de
 * link depois de decodificado (codificada, a regex não acha o `/pix/`).
 */
function redigirQuery(url: string): string {
  const inicio = url.indexOf('?');
  if (inicio === -1) return url;
  const hash = url.indexOf('#', inicio);
  const fim = hash === -1 ? url.length : hash;
  const pares = url
    .slice(inicio + 1, fim)
    .split('&')
    .map((par) => {
      const igual = par.indexOf('=');
      if (igual === -1) return par;
      const chave = par.slice(0, igual);
      if (PARAMS_COM_PEDIDO.has(decodificar(chave).toLowerCase())) return `${chave}=[REDACTED]`;
      const valor = decodificar(par.slice(igual + 1));
      const redigido = redactLinkCredentials(valor);
      return redigido === valor ? par : `${chave}=${encodeURIComponent(redigido)}`;
    });
  return `${url.slice(0, inicio + 1)}${pares.join('&')}${url.slice(fim)}`;
}

const sanitizarUrl = (url: string): string => redigirQuery(redactLinkCredentials(url));

function limparCabecalhos(headers: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [nome, valor] of Object.entries(headers ?? {})) {
    if (CABECALHOS_FORA.has(nome.toLowerCase())) continue;
    out[nome] = typeof valor === 'string' ? redactLinkCredentials(valor) : valor;
  }
  return out;
}

export function sanitizarRequisicao<T extends RequisicaoGravada>(dados: T): T {
  return {
    ...dados,
    url: sanitizarUrl(dados.url ?? ''),
    request: {
      ...dados.request,
      headers: limparCabecalhos(dados.request?.headers),
      body: redactLinkCredentialsDeep(dados.request?.body ?? null),
    },
    response: {
      ...dados.response,
      headers: limparCabecalhos(dados.response?.headers),
      body: redactLinkCredentialsDeep(dados.response?.body),
    },
  };
}

export const sanitizarUrlDaPagina = (url: string): string => sanitizarUrl(url);

/**
 * Página cujo caminho é credencial (`/alterar-cartao/<token>`, `/cartao/<código>`,
 * `/pix/<pedido>`, `/renovar/<token>`, `/r/<código>`): mesma lista do
 * `redactLinkCredentials`, para não ter duas.
 */
export const paginaComCredencial = (caminho: string): boolean =>
  redactLinkCredentials(caminho) !== caminho;
